import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { BookSearchResult } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { BookSearch } from "./BookSearch";
import { Cover, authorsOf } from "./BookBits";
import { CloseIcon } from "./Icons";
import { useScrollLock, useSheetDrag } from "./ui";
import { shelf } from "@pagemates/shared/copy";

const txt = shelf.pickBook;

/*
 * "Pick the first book" and "Add books you've read before": search the
 * catalog, confirm, and the book goes straight onto the shelf, as the
 * current book or onto Finished (with dates, if known). Admins only; one
 * call (POST books/place), so it's never a suggestion first.
 */
export function PickBook({ clubId, mode, first, onClose, onDone }:
  { clubId: string; mode: "current" | "finished"; first?: boolean; onClose: () => void; onDone?: () => void }) {
  useScrollLock();
  const qc = useQueryClient();
  const sheet = useRef<HTMLDivElement>(null);
  useSheetDrag(sheet, onClose);
  const [book, setBook] = useState<BookSearchResult | null>(null);
  const [startedAt, setStartedAt] = useState("");
  const [finishedAt, setFinishedAt] = useState("");
  const place = useMutation({
    mutationFn: () => api.placeBook(clubId, { hardcoverId: book!.hardcoverId, status: mode, ...(mode === "finished" ? { startedAt: startedAt || null, finishedAt: finishedAt || undefined } : {}) }),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ["books", clubId] }), qc.invalidateQueries({ queryKey: ["club-stats", clubId] }), qc.invalidateQueries({ queryKey: ["club-activity", clubId] })]);
      if (mode === "finished") { setBook(null); setStartedAt(""); setFinishedAt(""); } // add another, or close
      else onClose();
      onDone?.();
    },
  });
  const title = mode === "current" ? (first ? txt.pickTheFirstBook : txt.pickTheNextBook) : txt.addABookYou;
  return createPortal(
    <>
      <div className="modal-dim" onClick={onClose} />
      <div className="modal pick-sheet" role="dialog" aria-label={title} ref={sheet}>
        <div className="grab" />
        <div className="head"><b>{title}</b><button type="button" aria-label={txt.close} onClick={onClose}><CloseIcon /></button></div>
        <div className="pick-body">
          {!book ? (
            <>
              <p className="muted small" style={{ margin: 0 }}>{mode === "current" ? txt.itBecomesTheClub : txt.itGoesStraightOnto}</p>
              <BookSearch onPick={setBook} />
              {mode === "finished" && place.isSuccess && <p className="ok small" style={{ margin: 0 }}>{txt.addedSearchForAnother}</p>}
            </>
          ) : (
            <form className="stack" onSubmit={(e) => { e.preventDefault(); place.mutate(); }}>
              <div className="pick-chosen">
                <Cover book={book} size="sm" />
                <div style={{ minWidth: 0, flex: 1 }}><div className="t">{book.title}</div><div className="muted small">{authorsOf(book)}{book.releaseYear ? ` · ${book.releaseYear}` : ""}</div></div>
                <button type="button" className="btn ghost sm" onClick={() => setBook(null)}>{txt.change}</button>
              </div>
              {mode === "current" ? (
                <p style={{ margin: 0 }}>{txt.start}<i>{book.title}</i>{txt.forTheClub}</p>
              ) : (
                <div className="pick-dates">
                  <div className="field"><label htmlFor="pk-start">{txt.startedOptional}</label><input id="pk-start" className="input" type="date" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} /></div>
                  <div className="field"><label htmlFor="pk-end">{txt.finishedOptional}</label><input id="pk-end" className="input" type="date" value={finishedAt} onChange={(e) => setFinishedAt(e.target.value)} /></div>
                </div>
              )}
              {place.isError && <p className="error small">{describeError(place.error)}</p>}
              <div className="row" style={{ gap: 8 }}>
                <button type="submit" className="btn primary" disabled={place.isPending}>{place.isPending ? txt.working : mode === "current" ? txt.startIt : txt.addToFinished}</button>
                <button type="button" className="btn ghost" onClick={() => setBook(null)}>{txt.back}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
